import { getAuthProps, loginDone } from '@topconagriculture/auth';
import { getCountryByCode } from '@topconagriculture/country';
import { getErrorByCode, getErrors, queryCustomError, } from '@topconagriculture/graphql-client';
import { changeLanguage } from '@topconagriculture/language';
import { hideLoader, showLoader } from '@topconagriculture/loader';
import { closeAllModals } from '@topconagriculture/modal';
import i18next from 'i18next';
import { error, success } from 'react-notification-system-redux';
import { push } from 'redux-first-history';
import { getFormValues, stopSubmit } from 'redux-form';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { addUserQuery, getCompanyQuery, getTenantsSettingsQuery, getUserAppsQuery, getUserPermissionsQuery, getUserProfileById, PersonaBusinessScopes, updateCompanyQuery, updateUserQuery, } from '../api';
import { ProfilingForms, ProfilingLoaders } from '../config';
import { addUser, fetchTenantsSettings, fetchUserApps, fetchUserPermissions, getCompany, getProfile, retryMutationOnCompany, retryMutationOnCompanyCancel, retryMutationOnCompanySelectedRule, setUserProfileLoaded, updateCompany, updateUserProfile, } from './profiling.actions';
import { isCheckAgreementsOptions, } from './profiling.types';
import { getTenantSettings } from './profiling.selector';
import { getPersonaSetting } from '../utils';
export var UserUpdateErrors;
(function (UserUpdateErrors) {
    UserUpdateErrors["AlreadyExist"] = "AlreadyExist";
    UserUpdateErrors["LastUserAdmin"] = "LastUserAdmin";
})(UserUpdateErrors || (UserUpdateErrors = {}));
export var CompanyUpdateErrors;
(function (CompanyUpdateErrors) {
    CompanyUpdateErrors["InvalidAddress"] = "InvalidAddress";
    CompanyUpdateErrors["InvalidIdentifier"] = "InvalidIdentifier";
    CompanyUpdateErrors["EmailAlreadyExist"] = "EmailAlreadyExist";
    CompanyUpdateErrors["IndentifierAlreadyExist"] = "IndentifierAlreadyExist";
})(CompanyUpdateErrors || (CompanyUpdateErrors = {}));
export var ValidateAddressErrorCodes;
(function (ValidateAddressErrorCodes) {
    ValidateAddressErrorCodes["INVALID_ZIP"] = "INVALID_ZIP";
    ValidateAddressErrorCodes["INVALID_ZIP_AND_STATE"] = "INVALID_ZIP_AND_STATE";
    ValidateAddressErrorCodes["ADDRESS_VALIDATION_SERVICE_DOWN"] = "ADDRESS_VALIDATION_SERVICE_DOWN";
})(ValidateAddressErrorCodes || (ValidateAddressErrorCodes = {}));
export const errorKeys = {
    [ValidateAddressErrorCodes.ADDRESS_VALIDATION_SERVICE_DOWN]: 'profiling:errors.address_validation_service_down',
    [ValidateAddressErrorCodes.INVALID_ZIP]: 'profiling:errors.zip_code_not_valid',
    [ValidateAddressErrorCodes.INVALID_ZIP_AND_STATE]: 'profiling:errors.state_not_valid',
};
export function* getUserProfileSaga(gqlInstance, checkAgreementsOptions) {
    yield put(showLoader(ProfilingLoaders.userIsRetrievingProfile));
    yield put(setUserProfileLoaded(false));
    const authProps = yield select(getAuthProps);
    try {
        const result = yield call(getUserProfileById, gqlInstance, authProps.userId);
        if (isCheckAgreementsOptions(checkAgreementsOptions)) {
            const { expired, serviceAgreements } = result.agreements;
            if (expired || !serviceAgreements) {
                window.location.replace(checkAgreementsOptions.serviceAgreementsPageUrl);
            }
        }
        yield put(getProfile.done({
            params: authProps.userId,
            result,
        }));
        yield put(changeLanguage.started(result.locale));
    }
    catch (err) {
        yield put(getProfile.failed({
            params: authProps.userId,
            error: err,
        }));
        yield put(error({
            translationKey: 'user:errors:cant_load_user',
            autoDismiss: 2,
            position: 'bc',
        }));
        yield put(push('/error-page'));
    }
    yield put(setUserProfileLoaded(true));
    yield put(hideLoader(ProfilingLoaders.userIsRetrievingProfile));
}
export function* updateUserProfileSaga(gqlInstance) {
    yield put(showLoader(ProfilingLoaders.updatingUserProfile));
    const editFormValues = yield select(getFormValues(ProfilingForms.editUserProfile));
    try {
        const parsedPayload = {
            id: editFormValues.id,
            addressLine1: editFormValues.addressLine1,
            city: editFormValues.city,
            firstName: editFormValues.firstName,
            lastName: editFormValues.lastName,
            email: editFormValues.email,
            phone: editFormValues.phone,
            zipCode: editFormValues.zipCode,
            countryCode: editFormValues.countryCode,
            roles: editFormValues.roles,
            locale: editFormValues.locale,
            state: editFormValues.state,
            settings: {
                preferredAppId: editFormValues.settings.preferredAppId,
                regionalSettingsName: editFormValues.settings.regionalSettingsName,
            },
        };
        const result = yield call(updateUserQuery, gqlInstance, parsedPayload);
        const authProps = yield select(getAuthProps);
        if (editFormValues.id === authProps.userId) {
            yield put(getProfile.done({
                params: editFormValues.id,
                result,
            }));
        }
        yield put(updateUserProfile.done({
            params: editFormValues.id,
            result,
        }));
        yield put(success({
            translationKey: 'common:profiling:user_profile_updated_successfully',
            autoDismiss: 2,
            position: 'bc',
        }));
        yield put(closeAllModals());
    }
    catch (err) {
        yield put(updateUserProfile.failed({
            params: editFormValues.id,
            error: err,
        }));
        const errors = getErrors(err);
        if (queryCustomError(errors, { code: UserUpdateErrors.AlreadyExist })) {
            yield put(stopSubmit(ProfilingForms.editUserProfile, { email: i18next.t('profiling:errors.email_already_used') }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.email_already_used',
                position: 'bc',
            }));
        }
        else if (queryCustomError(errors, { code: UserCreateErrors.EmailDomainBlocked })) {
            yield put(stopSubmit(ProfilingForms.editUserProfile, { email: i18next.t('profiling:errors.email_domain_blocked') }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.email_domain_blocked',
                position: 'bc',
            }));
        }
        else if (queryCustomError(errors, { code: UserUpdateErrors.LastUserAdmin })) {
            yield put(stopSubmit(ProfilingForms.editUserProfile, { roles: '' }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.last_admin_user',
                position: 'bc',
            }));
        }
        else {
            yield put(error({
                translationKey: 'profiling:errors.cant_update_user',
                autoDismiss: 2,
                position: 'bc',
            }));
        }
    }
    yield put(hideLoader(ProfilingLoaders.updatingUserProfile));
}
export function* changeLanguageSaga(action) {
    const authProps = yield select(getAuthProps);
    if (authProps.userId === action.payload.params) {
        yield put(changeLanguage.started(action.payload.result.locale));
    }
}
function* terminateRetryMutationOnCompany(action) {
    const identifierType = action.payload.identifierType;
    yield put(stopSubmit(ProfilingForms.editCompany, {
        'billing.identifier': i18next.t('profiling:errors.indentifier_already_exists', {
            identifierType,
        }),
    }));
    yield put(hideLoader(ProfilingLoaders.updatingCompany));
}
export function* updateCompanySaga(gqlInstance, action) {
    yield put(showLoader(ProfilingLoaders.updatingCompany));
    const editFormValues = yield select(getFormValues(ProfilingForms.editCompany));
    const parsedPayload = {
        id: editFormValues.id,
        companyName: editFormValues.companyName,
        contactName: editFormValues.contactName,
        countryId: editFormValues.countryId,
        state: editFormValues.state,
        email: editFormValues.email,
        billing: {
            firstName: editFormValues.billing.firstName,
            lastName: editFormValues.billing.lastName,
            identifier: editFormValues.billing.identifier,
            identifierSelfGrant: editFormValues.billing.identifierSelfGrant,
            zipCode: editFormValues.billing.zipCode,
            city: editFormValues.billing.city,
            address: editFormValues.billing.address,
            phone: editFormValues.billing.phone,
            billingPeriod: editFormValues.billing.billingPeriod,
        },
        locale: editFormValues.locale,
    };
    try {
        const updateRule = action.payload && action.payload.updateRule ? action.payload.updateRule : undefined;
        const result = yield call(updateCompanyQuery, gqlInstance, parsedPayload, updateRule);
        const authProps = yield select(getAuthProps);
        if (editFormValues.id === authProps.companyId) {
            yield put(getCompany.done({
                params: editFormValues.id,
                result,
            }));
        }
        yield put(updateCompany.done({
            params: editFormValues.id,
            result,
        }));
        yield put(success({
            translationKey: 'profiling:company_updated_successfully',
            autoDismiss: 2,
            position: 'bc',
        }));
        yield put(closeAllModals());
    }
    catch (err) {
        yield put(updateCompany.failed({
            params: editFormValues.id,
            error: err,
        }));
        const errors = getErrors(err);
        if (queryCustomError(errors, { code: CompanyUpdateErrors.InvalidAddress })) {
            const { metadata } = getErrorByCode(err, CompanyUpdateErrors.InvalidAddress);
            let errorTranslationKey = 'profiling:errors.invalid_address';
            if (metadata.errorCode && ValidateAddressErrorCodes[metadata.errorCode]) {
                errorTranslationKey = errorKeys[ValidateAddressErrorCodes[metadata.errorCode]];
            }
            const fieldsErrors = {
                'billing.zipCode': 'profiling:errors.zip_code_not_valid',
                state: 'profiling:errors.state_not_valid',
                'billing.city': 'profiling:errors.city_not_valid',
            };
            const errorsMapped = {};
            if (metadata.wrongProperties) {
                metadata.wrongProperties.forEach((prop) => {
                    if (!fieldsErrors[prop])
                        return;
                    errorsMapped[prop] = i18next.t(fieldsErrors[prop]);
                });
            }
            if (Object.keys(errorsMapped).length) {
                yield put(stopSubmit(ProfilingForms.editCompany, errorsMapped));
            }
            yield put(error({
                autoDismiss: 5,
                translationKey: errorTranslationKey,
            }));
            yield put(hideLoader(ProfilingLoaders.updatingCompany));
            return;
        }
        else if (queryCustomError(errors, { code: CompanyUpdateErrors.IndentifierAlreadyExist })) {
            const countryByCodeSelector = yield select(getCountryByCode);
            const { companyIdentifier: identifierType } = countryByCodeSelector(parsedPayload.countryId);
            const personas = editFormValues.personas;
            const tenantSettings = yield select(getTenantSettings);
            if (getPersonaSetting(tenantSettings.personas, personas, 'businessScope') === PersonaBusinessScopes.DISTRIBUTOR) {
                yield put(retryMutationOnCompany());
            }
            else {
                yield put(stopSubmit(ProfilingForms.editCompany, {
                    'billing.identifier': i18next.t('profiling:errors.indentifier_already_exists', {
                        identifierType: identifierType,
                    }),
                }));
                yield put(error({
                    autoDismiss: 5,
                    translationKey: 'profiling:errors.indentifier_already_exists',
                    interpolationValues: { identifierType: identifierType },
                }));
            }
            yield put(hideLoader(ProfilingLoaders.updatingCompany));
            return;
        }
        else if (queryCustomError(errors, { code: CompanyUpdateErrors.InvalidIdentifier })) {
            const countryByCodeSelector = yield select(getCountryByCode);
            const { companyIdentifier: identifierType } = countryByCodeSelector(parsedPayload.countryId);
            yield put(stopSubmit(ProfilingForms.editCompany, {
                'billing.identifier': i18next.t('profiling:errors.invalid_identifier', {
                    identifierType: identifierType,
                }),
            }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.invalid_identifier',
                interpolationValues: { identifierType: identifierType },
            }));
            yield put(hideLoader(ProfilingLoaders.updatingCompany));
            return;
        }
        else {
            yield put(error({
                translationKey: 'profiling:errors.cant_update_company',
                autoDismiss: 2,
                position: 'bc',
            }));
        }
    }
    yield put(hideLoader(ProfilingLoaders.updatingCompany));
}
export function* getUserPermissionsSaga(gqlInstance, action) {
    yield put(showLoader(ProfilingLoaders.fetchPermissions));
    const authProps = yield select(getAuthProps);
    try {
        const response = yield call(getUserPermissionsQuery, gqlInstance, authProps.userId, action.payload);
        if (response) {
            yield put(fetchUserPermissions.done({
                params: authProps.userId,
                result: response,
            }));
        }
    }
    catch (err) {
        yield put(fetchUserPermissions.failed({
            params: authProps.userId,
            error: err,
        }));
        yield put(error({
            translationKey: 'user:error_load_permissions',
            autoDismiss: 2,
            position: 'bc',
        }));
    }
    yield put(hideLoader(ProfilingLoaders.fetchPermissions));
}
export function* getUserCompanySaga(gqlInstance) {
    yield put(showLoader(ProfilingLoaders.fetchCompany));
    const authProps = yield select(getAuthProps);
    try {
        const response = yield call(getCompanyQuery, gqlInstance, authProps.companyId);
        if (response) {
            yield put(getCompany.done({
                params: authProps.companyId,
                result: response,
            }));
        }
        else {
            yield put(error({
                translationKey: 'profiling:error_load_company',
                autoDismiss: 2,
                position: 'bc',
            }));
        }
    }
    catch (err) {
        yield put(getCompany.failed({
            params: authProps.companyId,
            error: err,
        }));
        yield put(error({
            translationKey: 'profiling:error_load_company',
            autoDismiss: 2,
            position: 'bc',
        }));
    }
    yield put(hideLoader(ProfilingLoaders.fetchCompany));
}
export function* getUserAppsSaga(gqlInstance) {
    yield put(showLoader(ProfilingLoaders.fetchApps));
    try {
        const { userId } = yield select(getAuthProps);
        const result = yield call(getUserAppsQuery, gqlInstance, userId);
        yield put(fetchUserApps.done({
            params: {},
            result,
        }));
    }
    catch (err) {
        yield put(fetchUserApps.failed({
            params: {},
            error: err,
        }));
        yield put(error({
            translationKey: 'profiling:errors.load_user_apps',
            autoDismiss: 2,
            position: 'bc',
        }));
    }
    yield put(hideLoader(ProfilingLoaders.fetchApps));
}
export function* getTenantsSettings(gqlInstance) {
    yield put(showLoader(ProfilingLoaders.fetchTenantsSettings));
    try {
        const result = yield call(getTenantsSettingsQuery, gqlInstance);
        yield put(fetchTenantsSettings.done({
            params: {},
            result,
        }));
    }
    catch (err) {
        yield put(fetchTenantsSettings.failed({
            params: {},
            error: err,
        }));
        yield put(error({
            translationKey: 'profiling:errors.load_tenants_settings',
            autoDismiss: 2,
            position: 'bc',
        }));
    }
    yield put(hideLoader(ProfilingLoaders.fetchTenantsSettings));
}
const toUserCreateInput = (userForm) => {
    const defaultPayload = {
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        email: userForm.email,
        countryCode: userForm.countryCode,
        locale: userForm.locale,
        state: userForm.state,
        addressLine1: userForm.addressLine1,
        addressLine2: userForm.addressLine2,
        phone: userForm.phone,
        city: userForm.city,
        zipCode: userForm.zipCode,
        roles: userForm.roles,
        settings: {
            dateFormat: 'YYYY-MM-DD',
            timeFormat: 'DD/MM/YYYY hh:mm',
            regionalSettingsName: userForm.settings.regionalSettingsName,
            preferredAppId: userForm.settings.preferredAppId,
        },
    };
    if (userForm.companyId) {
        defaultPayload.companyId = userForm.companyId;
    }
    if (userForm.tenantId) {
        defaultPayload.tenantId = userForm.tenantId;
    }
    if (userForm.personas) {
        defaultPayload.personas = userForm.personas;
    }
    return defaultPayload;
};
export var UserCreateErrors;
(function (UserCreateErrors) {
    UserCreateErrors["AlreadyExist"] = "AlreadyExist";
    UserCreateErrors["EmailDomainBlocked"] = "EmailDomainBlocked";
})(UserCreateErrors || (UserCreateErrors = {}));
export function* addUserSaga(gqlInstance, action) {
    const userForm = action.payload;
    const payload = toUserCreateInput(userForm);
    yield put(showLoader(ProfilingLoaders.addUser));
    try {
        const result = yield call(addUserQuery, gqlInstance, payload);
        yield put(closeAllModals());
        yield put(addUser.done({
            params: payload,
            result,
        }));
        yield put(success({
            translationKey: 'profiling:user_added_successfully',
            autoDismiss: 2,
            position: 'bc',
        }));
    }
    catch (err) {
        const errors = getErrors(err);
        if (queryCustomError(errors, { code: UserCreateErrors.AlreadyExist })) {
            yield put(stopSubmit(ProfilingForms.editUserProfile, { email: i18next.t('profiling:errors.email_already_used') }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.email_already_used',
                position: 'bc',
            }));
        }
        else if (queryCustomError(errors, { code: UserCreateErrors.EmailDomainBlocked })) {
            yield put(stopSubmit(ProfilingForms.editUserProfile, { email: i18next.t('profiling:errors.email_domain_blocked') }));
            yield put(error({
                autoDismiss: 5,
                translationKey: 'profiling:errors.email_domain_blocked',
                position: 'bc',
            }));
        }
        else {
            yield put(error({
                translationKey: 'profiling:errors.cant_create_user',
                autoDismiss: 2,
                position: 'bc',
            }));
        }
    }
    yield put(hideLoader(ProfilingLoaders.addUser));
}
export function watchProfilingSaga(gqlInstance, serviceAgreementsOptions) {
    return function* () {
        return yield all([
            yield takeLatest(loginDone, getUserProfileSaga, gqlInstance, serviceAgreementsOptions),
            yield takeLatest(loginDone, getUserCompanySaga, gqlInstance),
            yield takeLatest(loginDone, getTenantsSettings, gqlInstance),
            yield takeLatest(getProfile.done, getUserAppsSaga, gqlInstance),
            yield takeLatest(fetchUserPermissions.started, getUserPermissionsSaga, gqlInstance),
            yield takeLatest(getCompany.started, getUserCompanySaga, gqlInstance),
            yield takeLatest(updateUserProfile.started, updateUserProfileSaga, gqlInstance),
            yield takeLatest(updateUserProfile.done, changeLanguageSaga),
            yield takeLatest(updateCompany.started, updateCompanySaga, gqlInstance),
            yield takeLatest(addUser.started, addUserSaga, gqlInstance),
            yield takeLatest(fetchUserApps.started, getUserAppsSaga, gqlInstance),
            yield takeLatest(fetchTenantsSettings.started, getTenantsSettings, gqlInstance),
            yield takeLatest(retryMutationOnCompanySelectedRule, updateCompanySaga, gqlInstance),
            yield takeLatest(retryMutationOnCompanyCancel, terminateRetryMutationOnCompany),
        ]);
    };
}
//# sourceMappingURL=profiling.saga.js.map